import * as signalR from "@microsoft/signalr";
import { updateNotificationCount } from "../../Redux/Actions/Index";
import store from "../../Redux/Store/AppStore";
import { getToken } from "../../Utils/Auth";
import { PortalTypeEnum } from "../../Utils/Common/Enums/Enums";

class SignalRService {
  // Single connection
  private connection: signalR.HubConnection | null = null;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 2;
  private reconnectDelay: number = 3000;
  private isInitializing: boolean = false;

  /**
   * Get negotiate URL
   * Uses unified negotiate endpoint: /api/labs/{labId}/negotiate
   * Base URL changes based on environment (qa or production)
   */
  private getNegotiateUrl(): string {
    const state: any = store.getState();
    const labId = state?.Reducer?.selectedTenantInfo?.tenantId || 49;
    const environment = this.getEnvironment();
    
    // Set base URL based on environment
    const baseUrl = environment === "qa"
      ? "https://truemeditsignalrfunction-qa.azurewebsites.net"
      : "https://truemeditsignalrfunction.azurewebsites.net";

    return `${baseUrl}/api/labs/${labId}/negotiate`;
  }

  /**
   * Get the environment (qa or production) based on baseUrl
   */
  private getEnvironment(): string {
    const state: any = store.getState();
    const baseUrl = state?.Reducer?.baseUrl || "";

    // Check if baseUrl contains "qa" to determine environment
    if (baseUrl.toLowerCase().includes("qa")) {
      return "qa";
    }
    return "production";
  }

  /**
   * Initialize SignalR connection
   * Flow:
   * 1. Call negotiate endpoint to get connection URL
   * 2. Extract URL from negotiate response
   * 3. Create Hub connection with the negotiated URL
   * 4. Set up event handlers (listening to events with pattern: event-{environment}-{labId})
   * 5. Start the connection
   */
  async initializeConnection(): Promise<void> {
    // Check if PortalType is Admin - SignalR should only work for Admin portal
    const state: any = store.getState();
    const portalType = state?.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.adminType || 
                       state?.Reducer?.selectedTenantInfo?.infomationOfLoggedUser?.portalType;
    
    if (portalType !== PortalTypeEnum.Admin) {
      console.log("SignalR service is only available for Admin portal type");
      return;
    }

    // Prevent duplicate initialization
    if (this.isInitializing) {
      return;
    }

    // Check if connection already exists and is connected
    if (this.connection && this.connection.state === signalR.HubConnectionState.Connected) {
      return;
    }

    // If connection exists but is disconnected, clean it up first
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        // Ignore errors when stopping
      }
      this.connection = null;
    }

    this.isInitializing = true;

    try {
      const token = getToken();
      if (!token) {
        console.warn("No token available for SignalR connection");
        return;
      }

      // Step 1: Call negotiate endpoint
      const negotiateUrl = this.getNegotiateUrl();

      const response = await fetch(negotiateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Negotiate endpoint error:", {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
        });
        throw new Error(`Negotiate failed: ${response.status} ${response.statusText} - ${errorText}`);
      }

      // Try to parse as JSON, but handle text responses too
      let negotiateResponse: any;
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        negotiateResponse = await response.json();
      } else {
        const textResponse = await response.text();
        console.warn("Negotiate endpoint returned non-JSON response:", textResponse);
        try {
          negotiateResponse = JSON.parse(textResponse);
        } catch {
          throw new Error(`Negotiate endpoint returned invalid response: ${textResponse}`);
        }
      }

      // Step 2: Extract connection URL from negotiate response
      // The response might have different structures, so we check multiple possibilities
      // Note: Some APIs return capitalized field names (Url, AccessToken)
      let connectionUrl = negotiateResponse.Url 
      const accessToken = negotiateResponse.AccessToken 

      // Validate that we have the required connection URL
      if (!connectionUrl) {
        console.error("Negotiate response missing connection URL:", negotiateResponse);
        throw new Error("Negotiate response does not contain a connection URL");
      }

      // If accessToken is not provided, we might need to use the token from the request
      const finalAccessToken = accessToken || token;

      // Step 3: Create Hub connection with the negotiated URL
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(connectionUrl, {
          accessTokenFactory: () => finalAccessToken,
        })
        .withAutomaticReconnect({
          nextRetryDelayInMilliseconds: () => {
            if (this.reconnectAttempts < this.maxReconnectAttempts) {
              return this.reconnectDelay;
            }
            return null; // Stop reconnecting after max attempts
          },
        })
        .build();

      // Reset reconnect attempts
      this.reconnectAttempts = 0;

      // Step 4: Set up event handlers (listening to events with pattern: event-{environment}-{labId})
      this.setupEventHandlers();

      // Step 5: Start the connection
      await this.connection.start();
    } catch (error) {
      console.error("Error initializing SignalR connection:", error);
      this.reconnectAttempts++;

      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        setTimeout(() => this.initializeConnection(), this.reconnectDelay);
      }
    } finally {
      // Remove from initializing flag
      this.isInitializing = false;
    }
  }

  /**
   * Set up SignalR event handlers
   * Listens on events with pattern: event-{environment}-{labId}
   */
  private setupEventHandlers(): void {
    if (!this.connection) return;

    // Get environment and labId for event names
    const state: any = store.getState();
    const labId = state?.Reducer?.selectedTenantInfo?.tenantId;
    const environment = this.getEnvironment();

    if (!labId) {
      console.warn("No labId found, cannot set up event handlers");
      return;
    }

    const eventSuffix = `${environment}-${labId}`;

    // Handle reconnection
    this.connection.onreconnecting(() => {
      // Reconnecting...
    });

    this.connection.onreconnected(async () => {
      this.reconnectAttempts = 0;
    });

    this.connection.onclose((error) => {
      this.connection = null;

      // Only attempt reconnect if there was an error (not a manual disconnect)
      if (error) {
        this.reconnectAttempts++;
        if (this.reconnectAttempts < this.maxReconnectAttempts && !this.isInitializing) {
          setTimeout(() => this.initializeConnection(), this.reconnectDelay);
        }
      }
    });

    // Listen for sales rep pending count updates with environment-labId suffix
    this.connection.on(`salesRepPendingCountUpdated-${eventSuffix}`, (data: number | { count: number }) => {
      const count = typeof data === 'number' ? data : data.count;
      store.dispatch(updateNotificationCount({ salesRepRequest: count }));
    });

    // Listen for requisition pending count updates with environment-labId suffix
    this.connection.on(`requisitionPendingCountUpdated-${eventSuffix}`, (data: number | { count: number }) => {
      const count = typeof data === 'number' ? data : data.count;
      store.dispatch(updateNotificationCount({ pendingRequisition: count }));
    });

    // Listen for facility pending count updates with environment-labId suffix
    this.connection.on(`facilityPendingCountUpdated-${eventSuffix}`, (data: number | { count: number }) => {
      const count = typeof data === 'number' ? data : data.count;
      store.dispatch(updateNotificationCount({ newAccountRequest: count }));
    });
  }


  /**
   * Disconnect SignalR connection
   */
  async disconnect(): Promise<void> {
    if (this.connection) {
      try {
        await this.connection.stop();
      } catch (error) {
        console.error("Error stopping SignalR connection:", error);
      } finally {
        this.connection = null;
        this.reconnectAttempts = 0;
      }
    }
  }

  /**
   * Check if connection is active
   */
  isConnected(): boolean {
    return (
      this.connection !== null &&
      this.connection.state === signalR.HubConnectionState.Connected
    );
  }

}

// Export singleton instance
export const signalRService = new SignalRService();
export default signalRService;
